import { Copy, Reply } from 'lucide-react';
import { useState } from 'react';
import { useIsDark } from '../../contexts/ThemeContext';
import type { ProcessingNodeData } from '../../types/nodes';

/**
 * Configuration interface for response nodes
 */
interface ResponseConfigType {
	format: 'simple' | 'success' | 'data-only' | 'custom';
	template?: string;
	statusCode?: number;
	headers?: Record<string, string>;
	includeMetadata?: boolean;
}

/**
 * Props interface for response configuration component
 */
interface ResponseConfigProps {
	nodeId: string;
	data: ProcessingNodeData;
	onChange: (data: ProcessingNodeData) => void;
}

const defaultConfig: ResponseConfigType = {
	format: 'simple',
	statusCode: 200,
	includeMetadata: false,
	headers: {},
};

const formatTemplates = {
	simple: {
		name: 'Simple API Response',
		description: 'Clean statusCode + data format',
		example: `{
  "statusCode": 200,
  "data": { /* your data */ }
}`,
		template: `{
  "statusCode": 200,
  "data": {{upstream-data}}
}`,
	},
	success: {
		name: 'Success/Error Wrapper',
		description: 'Standard success response with data wrapper',
		example: `{
  "success": true,
  "data": { /* your data */ }
}`,
		template: `{
  "success": true,
  "data": {{upstream-data}}
}`,
	},
	'data-only': {
		name: 'Data Only',
		description: 'Return just the data without any wrapper',
		example: `{ /* your data directly */ }`,
		template: `{{upstream-data}}`,
	},
	custom: {
		name: 'Custom Template',
		description: 'Define your own response format',
		example: `{
  "result": {{upstream-data}},
  "timestamp": "{{execution.timestamp}}"
}`,
		template: `{
  "result": {{upstream-data}},
  "timestamp": "{{execution.timestamp}}"
}`,
	},
};

/**
 * Configuration component for response nodes
 */
export function ResponseConfig({ nodeId, data, onChange }: ResponseConfigProps) {
	const [copiedItem, setCopiedItem] = useState<string | null>(null);
	const isDark = useIsDark();

	// Initialize config with defaults
	const config: ResponseConfigType = {
		...defaultConfig,
		...data.config,
	};

	const handleConfigChange = (updates: Partial<ResponseConfigType>) => {
		const newConfig = { ...config, ...updates };
		onChange({ ...data, config: newConfig });
	};

	const copyToClipboard = async (text: string, item: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedItem(item);
			setTimeout(() => setCopiedItem(null), 2000);
		} catch (error) {
			console.error('Failed to copy to clipboard:', error);
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
					Response Configuration
				</h3>
				<p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
					Configure how data should be formatted for HTTP endpoint responses.
				</p>
			</div>

			{/* Response Format Selection */}
			<div className="space-y-4">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Response Format
				</label>

				<div className="grid gap-3">
					{Object.entries(formatTemplates).map(([format, template]) => (
						<button
							key={format}
							onClick={() => handleConfigChange({ format: format as any })}
							className={`p-4 rounded-lg border-2 text-left transition-all ${
								config.format === format
									? 'border-green-500 bg-green-50 dark:bg-green-900/20'
									: 'border-gray-200 dark:border-gray-600 hover:border-green-300'
							}`}
						>
							<div className="flex justify-between items-start">
								<div className="flex-1">
									<h4 className="mb-1 font-medium text-gray-900 dark:text-gray-100">
										{template.name}
									</h4>
									<p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
										{template.description}
									</p>
									<pre className="overflow-x-auto p-2 text-xs text-gray-700 bg-gray-100 rounded dark:text-gray-300 dark:bg-gray-700">
										<code>{template.example}</code>
									</pre>
								</div>
								{config.format === format && (
									<div className="flex justify-center items-center ml-3 w-5 h-5 bg-green-500 rounded-full">
										<span className="text-xs text-white">✓</span>
									</div>
								)}
							</div>
						</button>
					))}
				</div>
			</div>

			{/* Custom Template Editor */}
			{config.format === 'custom' && (
				<div className="space-y-3">
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
						Custom Template
					</label>
					<div className="grid grid-cols-2 gap-4 mb-3">
						<button
							onClick={() =>
								handleConfigChange({
									template: formatTemplates.simple.template,
									format: 'simple' as any,
								})
							}
							className="p-3 text-left border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
						>
							<div className="font-medium text-sm">Quick: statusCode + data</div>
							<div className="text-xs text-gray-500 mt-1">Your exact format</div>
						</button>
						<button
							onClick={() =>
								handleConfigChange({
									template: formatTemplates.success.template,
									format: 'success' as any,
								})
							}
							className="p-3 text-left border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
						>
							<div className="font-medium text-sm">Quick: success + data</div>
							<div className="text-xs text-gray-500 mt-1">Boolean success flag</div>
						</button>
					</div>
					<textarea
						value={config.template || formatTemplates.simple.template}
						onChange={e => handleConfigChange({ template: e.target.value })}
						placeholder={formatTemplates.simple.template}
						rows={6}
						className="px-3 py-2 w-full font-mono text-sm text-gray-900 bg-white rounded-md border border-gray-300 shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						💡 Use <code>{`{{upstream-data}}`}</code> to include all data from previous
						nodes
					</p>
				</div>
			)}

			{/* Status Code */}
			<div className="space-y-3">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					HTTP Status Code
				</label>
				<div className="flex items-center space-x-4">
					<input
						type="number"
						value={config.statusCode || 200}
						onChange={e => handleConfigChange({ statusCode: parseInt(e.target.value) })}
						min="100"
						max="599"
						className="px-3 py-2 w-20 text-gray-900 bg-white rounded-md border border-gray-300 shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
					/>
					<span className="text-sm text-gray-600 dark:text-gray-400">
						{config.statusCode === 200
							? '200 OK (Success)'
							: config.statusCode === 201
							? '201 Created'
							: config.statusCode === 400
							? '400 Bad Request'
							: config.statusCode === 404
							? '404 Not Found'
							: config.statusCode === 500
							? '500 Internal Server Error'
							: 'Custom Status Code'}
					</span>
				</div>
			</div>

			{/* Include Metadata Toggle */}
			<div className="space-y-3">
				<div className="flex items-center space-x-3">
					<input
						type="checkbox"
						id="includeMetadata"
						checked={config.includeMetadata !== false}
						onChange={e => handleConfigChange({ includeMetadata: e.target.checked })}
						className="w-4 h-4 text-green-600 bg-gray-100 rounded border-gray-300 focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
					/>
					<label
						htmlFor="includeMetadata"
						className="text-sm font-medium text-gray-700 dark:text-gray-300"
					>
						Include execution metadata
					</label>
				</div>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					Add execution ID, timestamp, and processing info to the response
				</p>
			</div>

			{/* Preview */}
			<div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
				<div className="flex justify-between items-center mb-3">
					<h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
						Response Preview
					</h4>
					<button
						onClick={() =>
							copyToClipboard(formatTemplates[config.format].example, 'preview')
						}
						className="flex gap-2 items-center px-3 py-1 text-xs text-white bg-green-600 rounded transition-colors hover:bg-green-700"
					>
						<Copy className="w-3 h-3" />
						{copiedItem === 'preview' ? 'Copied!' : 'Copy'}
					</button>
				</div>
				<pre className="overflow-x-auto p-3 text-xs text-gray-100 bg-gray-900 rounded">
					<code>{formatTemplates[config.format].example}</code>
				</pre>
			</div>

			{/* Usage Instructions */}
			<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
				<h4 className="flex items-center mb-2 text-sm font-medium text-blue-900 dark:text-blue-100">
					<Reply className="mr-2 w-4 h-4" />
					How to Use Response Nodes
				</h4>
				<ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
					<li>• Connect this node after your processing nodes</li>
					<li>• Configure the response format that your API should return</li>
					<li>• Select this node as the "Response Node" in your HTTP Endpoint trigger</li>
					<li>• The formatted response will be returned to API callers</li>
				</ul>
			</div>
		</div>
	);
}
